import { jwtDecode } from "jwt-decode";
import { getAccessToken } from "../utils";

/**
 * Get token expiration time in milliseconds
 * @param {string} token - JWT token (optional, will get from storage if not provided)
 * @returns {number|null} - Milliseconds until expiration, or null if token is invalid/expired
 */
export const getTokenExpirationTime = (token = null) => {
  try {
    const tokenToCheck = token || getAccessToken();
    if (!tokenToCheck) {
      return null;
    }

    const decoded = jwtDecode(tokenToCheck);
    const expirationTime = decoded.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;

    if (timeUntilExpiration <= 0) {
      return null;
    }

    return timeUntilExpiration > 0 ? timeUntilExpiration : null;
  } catch (err) {
    return null;
  }
};

/**
 * Check if token is close to expiration (within threshold)
 * @param {number} thresholdMs - Threshold in milliseconds (default: 60000 = 1 minute)
 * @param {string} token - JWT token (optional)
 * @returns {boolean} - True if token expires within threshold
 */
export const isTokenExpiringSoon = (thresholdMs = 60000, token = null) => {
  const timeUntilExpiration = getTokenExpirationTime(token);
  if (timeUntilExpiration === null) return true; // Expired or invalid
  
  return timeUntilExpiration <= thresholdMs;
};

/**
 * Get seconds until token expiration
 * @param {string} token - JWT token (optional)
 * @returns {number|null} - Seconds until expiration, or null if invalid/expired
 */
export const getSecondsUntilExpiration = (token = null) => {
  const timeUntilExpiration = getTokenExpirationTime(token);
  if (timeUntilExpiration === null) return null;
  
  return Math.ceil(timeUntilExpiration / 1000);
};

/**
 * Get token lifetime percentage remaining
 * @param {string} token - JWT token (optional)
 * @returns {number|null} - Percentage (0-100) of token lifetime remaining, or null if invalid
 */
export const getTokenLifetimePercentage = (token = null) => {
  try {
    const tokenToCheck = token || getAccessToken();
    if (!tokenToCheck) return null;

    const decoded = jwtDecode(tokenToCheck);
    const expirationTime = decoded.exp * 1000;
    const issuedTime = decoded.iat * 1000; // Token issued at time
    const currentTime = Date.now();
    
    const totalLifetime = expirationTime - issuedTime;
    const remainingTime = expirationTime - currentTime;
    
    if (totalLifetime <= 0 || remainingTime < 0) return null;
    
    return (remainingTime / totalLifetime) * 100;
  } catch (err) {
    return null;
  }
};

/**
 * Check if token expired recently (within warning window)
 * @param {number} warningWindowSeconds - Warning window in seconds (default: 60 = 1 minute)
 * @param {string} token - JWT token (optional)
 * @returns {boolean|null} - True if token expired within warning window, false if expired earlier, null if invalid/not expired
 */
export const isTokenExpiredRecently = (warningWindowSeconds = 60, token = null) => {
  try {
    const tokenToCheck = token || getAccessToken();
    if (!tokenToCheck) return null;

    const decoded = jwtDecode(tokenToCheck);
    const expirationTime = decoded.exp * 1000;
    const currentTime = Date.now();
    const secondsSinceExpiration = Math.floor((currentTime - expirationTime) / 1000);
    
    // If token hasn't expired yet, return null
    if (secondsSinceExpiration < 0) return null;
    
    // If token expired within warning window, return true
    return secondsSinceExpiration <= warningWindowSeconds;
  } catch (err) {
    return null;
  }
};

